export type BiometricType = 'faceId' | 'touchId' | 'unknown';

/**
 * Detects whether the device likely uses Face ID or Touch ID
 * based on device characteristics (screen dimensions, user agent)
 */
export const detectBiometricType = (): BiometricType => {
  // Only relevant in Despia environment
  if (!navigator.userAgent.includes('despia')) {
    return 'unknown';
  }

  const { screen } = window;
  const screenWidth = screen.width;
  const screenHeight = screen.height;
  
  const isIPhone = /iPhone/.test(navigator.userAgent);
  const isIPad = /iPad/.test(navigator.userAgent);
  
  if (isIPhone) {
    // Face ID iPhones have notch/dynamic island and specific aspect ratios
    // Screen height >= 812 (iPhone X and later in portrait)
    // Aspect ratio roughly 19.5:9 or similar
    const aspectRatio = Math.max(screenHeight, screenWidth) / Math.min(screenHeight, screenWidth);
    
    // Face ID devices have aspect ratio > 2.0 (approximately 2.16)
    // Touch ID devices have aspect ratio around 1.78 (16:9)
    if (aspectRatio > 2.0 || screenHeight >= 812) {
      return 'faceId';
    }
    return 'touchId';
  }
  
  if (isIPad) {
    // iPad Pro 11" and 12.9" (3rd gen+) have Face ID
    // Check for larger screen sizes typical of Face ID iPads
    const screenDiagonal = Math.sqrt(screenWidth ** 2 + screenHeight ** 2);
    // Face ID iPads have larger logical resolutions
    if (screenDiagonal > 1400) {
      return 'faceId';
    }
    return 'touchId';
  }
  
  return 'unknown';
};

/**
 * Gets a user-friendly label for the biometric type
 */
export const getBiometricLabel = (type: BiometricType): string => {
  switch (type) {
    case 'faceId':
      return 'Face ID';
    case 'touchId':
      return 'Touch ID';
    default:
      return 'Biometrics';
  }
};
